/**
 * Windows a workspace shows at most once. Opening one while it is already on
 * screen shows the open window (its props updated, restored, raised) instead
 * of stacking a copy. Every opener mints its own id, so id-only deduplication
 * let each tap, assistant turn or picker add another copy; the copies were
 * then saved and came back together on the next sign-in.
 *
 * Settings switches sections inside one window, the support inbox lists every
 * ticket, the mailbox switches Inbox, Spam and Trash in place, and the clock
 * shows every timer and alarm, so a second copy of any of them never shows
 * anything the first cannot.
 */
export const SINGLETON_WINDOW_NAMES: ReadonlySet<string> = new Set([
  "Settings",
  "SupportInbox",
  "Mailbox",
  "ClockViewer",
]);

export function isSingletonWindowName(name: unknown): name is string {
  return typeof name === "string" && SINGLETON_WINDOW_NAMES.has(name);
}

type WindowLike = {
  id?: unknown;
  name?: unknown;
  props?: unknown;
  zIndex?: unknown;
  isMinimized?: unknown;
};

function zIndexOf(component: WindowLike): number {
  return typeof component.zIndex === "number" && Number.isFinite(component.zIndex) ? component.zIndex : 0;
}

/**
 * One window per singleton name. The copy kept is the one the user saw last:
 * open over minimized, then the highest z-index, then the latest in the list.
 * Everything else keeps its order.
 */
export function collapseSingletonWindows<T extends WindowLike>(components: readonly T[]): { kept: T[]; removed: T[] } {
  const keeperByName = new Map<string, T>();
  for (const component of components) {
    if (!isSingletonWindowName(component?.name)) continue;
    const current = keeperByName.get(component.name);
    if (!current) {
      keeperByName.set(component.name, component);
      continue;
    }
    const currentOpen = current.isMinimized !== true;
    const candidateOpen = component.isMinimized !== true;
    if (currentOpen !== candidateOpen) {
      if (candidateOpen) keeperByName.set(component.name, component);
      continue;
    }
    if (zIndexOf(component) >= zIndexOf(current)) keeperByName.set(component.name, component);
  }
  const kept: T[] = [];
  const removed: T[] = [];
  for (const component of components) {
    if (isSingletonWindowName(component?.name) && keeperByName.get(component.name) !== component) removed.push(component);
    else kept.push(component);
  }
  return { kept, removed };
}

/**
 * Props that belong to the opener, not to the window. Each one says where the
 * opener wants the window to land (a section, a focused card, the providers
 * it asked about). A reused window keeps no stale destination: an opener that
 * names any of them clears the ones it leaves out. Settings shows
 * `activeGroup` before `type`, and expands `focusedConnection`, so a Connect
 * that asks only for the sync page must not land on the card the previous
 * opener focused.
 */
const OPENER_SCOPED_PROPS: Readonly<Record<string, readonly string[]>> = {
  Settings: ["activeGroup", "focusedConnection", "providerNames", "categories", "preparationRequestId"],
};
/** Props that name a destination; an open request without any just shows the window as it is. */
const DESTINATION_PROPS: Readonly<Record<string, readonly string[]>> = {
  Settings: ["type", ...OPENER_SCOPED_PROPS.Settings],
};

/**
 * The props to apply when an open request shows a window already on screen:
 * the request's props without its own id, with every opener-scoped prop it
 * leaves out cleared.
 */
export function singletonReuseProps(name: string, rawProps: unknown): Record<string, unknown> {
  const props = rawProps && typeof rawProps === "object" && !Array.isArray(rawProps)
    ? { ...rawProps as Record<string, unknown> }
    : {};
  delete props.id;
  const scoped = OPENER_SCOPED_PROPS[name];
  if (scoped && (DESTINATION_PROPS[name] ?? scoped).some((key) => key in props)) {
    for (const key of scoped) if (!(key in props)) props[key] = undefined;
  }
  return props;
}

export type SingletonWindowReuse = {
  /** The id of the window already on screen. */
  existingId: string;
  /** The props to apply to it (see `singletonReuseProps`). */
  props: Record<string, unknown>;
};

/**
 * Splits an add request into windows to create and singleton windows to show
 * instead. An incoming window whose id is already on screen is left to the
 * caller's id check. A second copy inside the same request is dropped.
 */
export function planSingletonWindowAdds<T extends WindowLike>(
  existing: readonly WindowLike[],
  incoming: readonly T[],
): { toAdd: T[]; reuse: SingletonWindowReuse[] } {
  const existingIds = new Set(existing.map((component) => component?.id));
  const openByName = new Map<string, string>();
  for (const component of existing) {
    if (isSingletonWindowName(component?.name) && typeof component.id === "string" && !openByName.has(component.name)) {
      openByName.set(component.name, component.id);
    }
  }
  const toAdd: T[] = [];
  const reuse: SingletonWindowReuse[] = [];
  const addedNames = new Set<string>();
  for (const component of incoming) {
    if (!component || !isSingletonWindowName(component.name) || existingIds.has(component.id)) {
      if (component) toAdd.push(component);
      continue;
    }
    const existingId = openByName.get(component.name);
    if (existingId) {
      const props = singletonReuseProps(component.name, component.props);
      reuse.push({ existingId, props });
      continue;
    }
    if (addedNames.has(component.name)) continue;
    addedNames.add(component.name);
    toAdd.push(component);
  }
  return { toAdd, reuse };
}

/** Whether applying `update` would change any prop of `current`. */
export function propsWouldChange(current: unknown, update: Record<string, unknown>): boolean {
  const base = current && typeof current === "object" ? current as Record<string, unknown> : {};
  return Object.entries(update).some(([key, value]) => !Object.is(base[key], value)
    && JSON.stringify(base[key] ?? null) !== JSON.stringify(value ?? null));
}
